import { useEffect, useRef, useState } from "react";
import s from "./Main.module.scss";
import { homeText } from "../i18n/home";
import { CLOUDINARY_BASE_URL } from "../config/cloudinary";
import { useLanguage } from "../contexts/LanguageContext";

const VIDEO_ASPECT_RATIO = 16 / 9;

const heroImagePath =
  "v1785476394/00080728-DJI_20260728114443_0018_D-light_typqqr.webp";

const heroYoutubeUrl =
  "https://www.youtube.com/embed/9DWILtJ2vik?autoplay=1&mute=1&loop=1&playlist=9DWILtJ2vik&controls=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&fs=0&disablekb=1&vq=hd2160";

const cloudinaryImage = (path, transform) => {
  const base = CLOUDINARY_BASE_URL.replace(/\/$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");

  return `${base}/${transform}/${cleanPath}`;
};

const heroImage = cloudinaryImage(
  heroImagePath,
  "f_auto,q_auto:best,w_2400,c_limit",
);

const Main = () => {
  const { lang } = useLanguage();
  const t = homeText[lang];
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoSize, setVideoSize] = useState(null);
  const rootRef = useRef(null);

  const handleVideoLoad = () => {
    // The iframe's own onLoad only means the embed document arrived — YouTube's
    // player still shows its static title/thumbnail card for a beat after that
    // before the muted autoplay actually starts rendering frames. Waiting a bit
    // longer before revealing it avoids flashing that title card over the hero.
    window.setTimeout(() => setIsVideoReady(true), 900);
  };

  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return undefined;

    // The hero section's height is driven by its text content, not the
    // viewport, so sizing the video off vw/vh (as a plain CSS "cover" trick
    // would) can leave it shorter than the section on mobile, exposing the
    // background above/below it. Measure the actual section instead.
    const updateVideoSize = () => {
      const { width, height } = rootEl.getBoundingClientRect();
      if (!width || !height) return;

      const containerAspect = width / height;
      if (containerAspect > VIDEO_ASPECT_RATIO) {
        setVideoSize({ width, height: width / VIDEO_ASPECT_RATIO });
      } else {
        setVideoSize({ width: height * VIDEO_ASPECT_RATIO, height });
      }
    };

    updateVideoSize();

    const resizeObserver = new ResizeObserver(updateVideoSize);
    resizeObserver.observe(rootEl);
    return () => resizeObserver.disconnect();
  }, [lang]);

  return (
    <section className={s.root} ref={rootRef}>
      <img
        src={heroImage}
        alt=""
        className={s.heroImage}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        aria-hidden="true"
      />

      <div
        className={`${s.heroYoutube} ${isVideoReady ? s.heroYoutubeReady : ""}`}
        aria-hidden="true"
      >
        <iframe
          src={heroYoutubeUrl}
          title="Miyako tour hero movie"
          allow="autoplay; encrypted-media; picture-in-picture"
          tabIndex="-1"
          onLoad={handleVideoLoad}
          style={
            videoSize
              ? { width: `${videoSize.width}px`, height: `${videoSize.height}px` }
              : undefined
          }
        />
      </div>

      <div className={s.heroContent}>
        <h1 className={s.heroTitle}>
          Create <br />
          <span className={s.unforgettable}>Unforgettable</span>
          <br />
          <span className={s.memories}>Memories</span>
        </h1>

        <p className={s.heroText}>{t.description}</p>
      </div>

      <div className={s.wave}>
        <svg viewBox="0 0 2880 180" preserveAspectRatio="none">
          <path
            d="
              M0,70
              C320,240 560,-40 820,105
              C1080,220 1260,5 1440,90

              C1760,240 2000,-40 2260,105
              C2520,220 2700,5 2880,90

              L2880,180
              L0,180
              Z
            "
          />
        </svg>
      </div>
    </section>
  );
};

export default Main;
